/* ============================================================
   JERKIN' IT v2.0 — CORE
   Shared utilities: money, toast, focus trap, drawers, analytics,
   header state, and the Easter eggs that live outside the cart.
   No third-party dependencies.
   ============================================================ */
(() => {
  'use strict';

  const q = (sel, ctx = document) => ctx.querySelector(sel);
  const qa = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Money: always the shop's own format ---------- */
  function formatWithDelimiters(cents, precision = 2, thousands = ',', decimal = '.') {
    if (!isFinite(cents) || cents == null) return '0';
    const value = (cents / 100.0).toFixed(precision);
    const parts = value.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return dollars + (parts[1] ? decimal + parts[1] : '');
  }
  function money(cents) {
    const format = window.JI?.moneyFormat || '${{amount}}';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholder);
    if (!match) return format;
    let value;
    switch (match[1]) {
      case 'amount': value = formatWithDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = formatWithDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = formatWithDelimiters(cents, 0, ' ', ','); break;
      case 'amount_with_apostrophe_separator': value = formatWithDelimiters(cents, 2, "'", '.'); break;
      default: value = formatWithDelimiters(cents, 2);
    }
    return format.replace(placeholder, value).replace(/<[^>]*>/g, '').trim();
  }

  const escapeHtml = (value) => {
    const d = document.createElement('div');
    d.textContent = value ?? '';
    return d.innerHTML;
  };

  /* ---------- Toast: one implementation ---------- */
  let toastTimer;
  function toast(message) {
    const el = q('[data-ji-toast]');
    if (!el || !message) return;
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 2200);
  }

  /* ---------- Analytics: one seam for every custom event ---------- */
  function track(name, payload = {}) {
    const detail = { event: 'ji:' + name, ...payload };
    document.dispatchEvent(new CustomEvent('ji:track', { detail, bubbles: true }));
    if (Array.isArray(window.dataLayer)) window.dataLayer.push(detail);
  }

  /* ---------- Focus trap + drawer controller ---------- */
  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  class Drawer {
    constructor(panel, { overlay, onOpen } = {}) {
      this.panel = panel;
      this.overlay = overlay;
      this.onOpen = onOpen;
      this.isOpen = false;
      this.lastFocus = null;
      this._keydown = this._keydown.bind(this);
    }
    open() {
      if (!this.panel || this.isOpen) return;
      this.isOpen = true;
      this.lastFocus = document.activeElement;
      this.panel.classList.add('is-open');
      this.panel.setAttribute('aria-hidden', 'false');
      this.panel.setAttribute('aria-modal', 'true');
      this.panel.setAttribute('role', 'dialog');
      this.overlay?.classList.add('is-open');
      document.documentElement.classList.add('ji-locked');
      this._setInert(true);
      document.addEventListener('keydown', this._keydown);
      this.onOpen?.();
      requestAnimationFrame(() => {
        const target = this.panel.querySelector('[data-autofocus]') || this.panel.querySelector(FOCUSABLE);
        target?.focus();
      });
    }
    close() {
      if (!this.panel || !this.isOpen) return;
      this.isOpen = false;
      this.panel.classList.remove('is-open');
      this.panel.setAttribute('aria-hidden', 'true');
      this.panel.removeAttribute('aria-modal');
      this.overlay?.classList.remove('is-open');
      document.documentElement.classList.remove('ji-locked');
      this._setInert(false);
      document.removeEventListener('keydown', this._keydown);
      if (this.lastFocus && document.contains(this.lastFocus)) this.lastFocus.focus();
    }
    toggle() { this.isOpen ? this.close() : this.open(); }
    // Background content is made inert so tabbing cannot escape the drawer.
    _setInert(on) {
      [...document.body.children].forEach(node => {
        if (node === this.panel || node === this.overlay || node.contains(this.panel)) return;
        if (node.hasAttribute('data-ji-toast')) return;
        if (on) node.setAttribute('inert', ''); else node.removeAttribute('inert');
      });
    }
    _keydown(e) {
      if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
      if (e.key !== 'Tab') return;
      const items = qa(FOCUSABLE, this.panel).filter(el => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  /* ---------- Header scroll state ---------- */
  const header = q('[data-ji-header]');
  if (header) {
    const run = () => header.classList.toggle('is-scrolled', window.scrollY > 24);
    run();
    addEventListener('scroll', run, { passive: true });
  }

  /* ---------- Mobile menu ---------- */
  const menuPanel = q('[data-ji-menu]');
  const menu = menuPanel ? new Drawer(menuPanel) : null;
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-ji-menu-open]')) { e.preventDefault(); menu?.open(); }
    else if (e.target.closest('[data-ji-menu-close]')) { e.preventDefault(); menu?.close(); }
  });

  /* ============================================================
     EASTER EGGS
     Cosmetic only. Never touch price, cart contents or checkout.
     All reduced-motion safe. None repeat within a session.
     ============================================================ */

  // 1. Logo occasionally offers an alternate tagline.
  const logoAlt = q('[data-ji-logo-alt]');
  if (logoAlt) {
    const lines = (logoAlt.dataset.lines || '').split('|').filter(Boolean);
    const logo = logoAlt.closest('a') || logoAlt.parentElement;
    if (lines.length && logo) {
      let shown = false;
      const reveal = () => {
        if (shown || Math.random() > 0.35) return;
        shown = true;
        logoAlt.textContent = lines[Math.floor(Math.random() * lines.length)];
        setTimeout(() => { logoAlt.textContent = ''; shown = false; }, 2600);
      };
      logo.addEventListener('mouseenter', reveal);
      logo.addEventListener('touchstart', reveal, { passive: true });
    }
  }

  // 2. Tab title changes once when the customer leaves the tab.
  (() => {
    const away = document.body.dataset.jiAwayTitle;
    if (!away) return;
    let original = document.title;
    let used = false;
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && !used) { original = document.title; document.title = away; used = true; }
      else if (!document.hidden) { document.title = original; }
    });
  })();

  // 3. 7 Pot Punch heat indicator reacts on hover/focus. No flashing.
  document.addEventListener('pointerover', (e) => {
    const heat = e.target.closest?.('[data-ji-heat-live]');
    if (heat && !reduceMotion()) heat.classList.add('is-hot');
  });
  document.addEventListener('pointerout', (e) => {
    const heat = e.target.closest?.('[data-ji-heat-live]');
    if (heat) heat.classList.remove('is-hot');
  });

  // 4. Typed keyword flourish. Cosmetic, single-shot, ignores form fields.
  (() => {
    const word = 'jerky';
    let buffer = '';
    document.addEventListener('keydown', (e) => {
      const tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target.isContentEditable) return;
      if (e.key.length !== 1) return;
      buffer = (buffer + e.key.toLowerCase()).slice(-word.length);
      if (buffer !== word || document.documentElement.dataset.jiEgg) return;
      document.documentElement.dataset.jiEgg = 'on';
      toast('Nice. You typed it out loud.');
      track('easter_egg', { egg: 'typed_keyword' });
    });
  })();

  /* ---------- Public surface ---------- */
  window.JI = Object.assign(window.JI || {}, {
    money, toast, track, escapeHtml, Drawer, q, qa, reduceMotion
  });
  window.JIToast = toast;
})();
